import * as R from 'ramda'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import {
  EventDeleteMessagesPayload,
  IMessage,
  Message,
  WithBodyMessage,
} from '@weblite/ts-action/message'
import { Subscription } from '@weblite/ts-action/subscription'
import { Pv } from '@weblite/ts-action/pv'
import { WappRoom } from '@weblite/ts-action/wapp-room'
import { getAquaManager } from '@weblite/aqua'
import { getRoomLastScroll } from '$logic/room-last-scroll'
import { canEditMessage, getMessageByIid, getMessages } from '$logic/message'
import { sendFieldApis } from '$comp/_app/components/chat/components/footer/components/send-field'
import { getRoom, getStorageByRoomTypeAndRoomId } from '$logic/room'
import { showLoadingMessagesModal } from '$comp/modals/loading-modal'
import { getBoundaryData } from '$func/utils'
import {
  getRoomLastMessage,
  updateLastMessageEvent,
} from '$logic/room-last-message'
import { ROOM_MESSAGE_LIST_AQUA_MANAGER_ID } from '$comp/_app/components/chat/components/body/components/messageList/message-list'
import { deleteFromArrayEnd, deleteFromArrayStart } from '$func/iteratees'
import {
  getOpenedRoom,
  getOpenedRoomData,
  getOpenedRoomId,
} from './opened-room.service'
import { openedRoomStorage } from './opened-room.storage'
import { openedRoomMessagesStorage } from './opened-room-messages.storage'
import {
  createUserWaitReporter,
  UserWaitPendingAction,
} from '$logic/analytics/reporters/user-wait'
import { loadChatUserWaitReporter } from '$comp/_app/components/chat/components/body/components/messageList/wait-reporter'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import { lastOpenedRoomMessagesService } from '$logic/last-opened-room-messages/service'

const BUCKET_SIZE = 20
const MAXIMUM_RENDERED_MESSAGES_COUNT = 60
const MAXIMUM_MESSAGE_COUNT_IN_VIEW_PORT = 20

// `start` & `end` are inclusive
function calculateMessageFellows(
  iid: number,
  lastIid: number,
  block: 'center' | 'start',
): [number, number] {
  if (block === 'center') {
    const end = Math.min(iid + BUCKET_SIZE / 2, lastIid || 1)
    const start = Math.max(1, end - BUCKET_SIZE + 1)
    return [start, end]
  }
  const end = Math.min(iid + BUCKET_SIZE - 1, lastIid || 1)
  const start = Math.max(1, end - BUCKET_SIZE)
  return [start, end]
}

function insertMessagesToRoomPage(messages: Message[]) {
  const roomMessages = openedRoomMessagesStorage.getAll()
  // NOTE: Checking if room changed.
  const notDeletedNewMessagesLength = messages.filter(
    message => message.patches?.deleted !== true,
  ).length
  const notDeletedOldMessagesLength = roomMessages.filter(
    message => message.patches?.deleted !== true,
  ).length

  if (messages[0]?.roomId !== roomMessages[0]?.roomId) {
    openedRoomMessagesStorage.replace(messages)
    return
  }

  // for keeping rendered messages length under the maximum count (virtualization)
  const diffWithMaximumRenderedMessages =
    notDeletedNewMessagesLength +
    notDeletedOldMessagesLength -
    MAXIMUM_RENDERED_MESSAGES_COUNT

  if (R.last(messages)?.iid === roomMessages[0]?.iid - 1) {
    // adding old messages on top of current messages
    if (diffWithMaximumRenderedMessages > 0) {
      openedRoomMessagesStorage.replace([
        ...messages,
        ...deleteFromArrayEnd(
          roomMessages,
          diffWithMaximumRenderedMessages,
          message => message.patches?.deleted,
        ),
      ])
    } else {
      openedRoomMessagesStorage.replace([...messages, ...roomMessages])
    }
  } else if (R.last(roomMessages)?.iid === messages[0]?.iid - 1) {
    // adding new messages to bottom of current messages
    if (diffWithMaximumRenderedMessages > 0)
      openedRoomMessagesStorage.replace([
        ...deleteFromArrayStart(
          roomMessages,
          diffWithMaximumRenderedMessages,
          message => message.patches?.deleted,
        ),
        ...messages,
      ])
    else openedRoomMessagesStorage.replace([...roomMessages, ...messages])
  } else {
    const roomMessagesBoundary = getBoundaryData(roomMessages, R.prop('iid'))
    const messagesBoundary = getBoundaryData(messages, R.prop('iid'))

    // If messages do not have overlap, replace new messages
    if (
      messagesBoundary.end < roomMessagesBoundary.start ||
      messagesBoundary.start > roomMessagesBoundary.end
    )
      openedRoomMessagesStorage.replace(messages)
    else {
      const unionFunc = R.eqBy(R.prop('iid'))
      const union =
        messagesBoundary.start < roomMessagesBoundary.start
          ? R.unionWith(unionFunc, messages, roomMessages)
          : R.unionWith(unionFunc, roomMessages, messages)

      // insert union of old and new messages
      openedRoomMessagesStorage.replace(union)
    }
  }
}

async function fillRoomViewPortWithMessage(
  loadedMessages: Message[],
  roomId: string,
) {
  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-inFillRoomViewPortWithMessageLoadedMessagesLength:`]:
      loadedMessages.length,
  })
  const messages = loadedMessages.filter(msg => !msg.patches?.deleted)
  if (messages.length < MAXIMUM_MESSAGE_COUNT_IN_VIEW_PORT) {
    const lastMessage = await getRoomLastMessage(roomId)
    const room = await getRoom(roomId)
    if (!room || !lastMessage) return

    const { get } = getStorageByRoomTypeAndRoomId(roomId, room.type)
    const { firstVisibleIid } = await get(roomId)

    const { start, end } = getBoundaryData(loadedMessages, R.prop('iid'))
    const shouldFetchCount = BUCKET_SIZE - messages.length

    let newMessages: Message[]

    if (lastMessage.iid - end > shouldFetchCount) {
      newMessages = await getMessages(roomId, {
        iid: end,
        position: 'NEXT',
        count: shouldFetchCount,
      })
      fillRoomViewPortWithMessage([...messages, ...newMessages], roomId)
    } else if (start - firstVisibleIid > shouldFetchCount) {
      newMessages = await getMessages(roomId, {
        iid: start,
        position: 'PREVIOUS',
        count: shouldFetchCount,
      })
      fillRoomViewPortWithMessage([...newMessages, ...messages], roomId)
    } else {
      newMessages = await getMessages(roomId, {
        iid: lastMessage.iid + 1,
        position: 'PREVIOUS',
        count: BUCKET_SIZE,
      })
    }

    if (roomId === getOpenedRoomId()) insertMessagesToRoomPage(newMessages)
  }
}

async function loadMessageFellows(
  iid: number,
  block: 'center' | 'start' = 'center',
  onStartFetchingFromServer?: () => void,
) {
  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-inLoadMessageFellows`]: true,
  })

  const openedRoomId = await getOpenedRoomId()
  const room = await getRoom(openedRoomId)

  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-inLoadMessageFellowsGotRoomIid`]: room?.iid,
  })

  if (!room) return

  const range = calculateMessageFellows(iid, room.iid, block)

  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-calculatedMessageFellows`]: `${range[0]}-${range[1]}`,
  })

  let messages: Message[]

  if (range[1] === room.iid) {
    messages = await getMessages(
      openedRoomId,
      {
        iid: room.iid + 1,
        position: 'PREVIOUS',
        count: BUCKET_SIZE,
      },
      onStartFetchingFromServer,
    )
  } else if (range[0] === 1) {
    messages = await getMessages(
      openedRoomId,
      {
        iid: 0,
        position: 'NEXT',
        count: BUCKET_SIZE,
      },
      onStartFetchingFromServer,
    )
  } else {
    messages = await getMessages(
      openedRoomId,
      {
        iid: range[1] + 1,
        position: 'PREVIOUS',
        count: BUCKET_SIZE,
      },
      onStartFetchingFromServer,
    )
    fillRoomViewPortWithMessage(messages, openedRoomId)
  }

  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-endLoadMessageFellowsFirstGetMessages`]: true,
  })

  await getMessages(openedRoomId, { range })

  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-endLoadMessageFellowsSecondGetMessages`]: true,
  })

  if (openedRoomId === getOpenedRoomId()) insertMessagesToRoomPage(messages)

  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-endLoadMessageFellows`]: true,
  })
}

export async function shouldLoadMessages(position: 'PREVIOUS' | 'NEXT') {
  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-inShouldLoadMessages`]: position,
  })
  const messages = openedRoomMessagesStorage.getAll()

  const room = await getOpenedRoom()

  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-inShouldLoadMessagesGotRoom`]: true,
  })
  if (!room) return false

  const lastMessage = await getRoomLastMessage(room.id)
  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-inShouldLoadMessagesGotLastMessage`]:
      lastMessage?.iid,
  })
  if (!messages.length) return !!lastMessage

  if (room.id !== messages[0].roomId) return true

  const data = await getStorageByRoomTypeAndRoomId(room.id, room.type).get(
    room.id,
  )

  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-inShouldLoadMessagesGotStorageByRoomTypeAndRoomId`]:
      true,
  })

  if (position === 'PREVIOUS') {
    return messages[0].iid > (data?.firstVisibleIid || 1)
  }

  const lastValidMessageIid =
    (data && 'lastVisibleIid' in data && data.lastVisibleIid) ||
    lastMessage?.iid ||
    0

  const lasMessage = R.last(messages)
  return !!lasMessage && lasMessage.iid < lastValidMessageIid
}

const loadMoreMessagesReporter = createUserWaitReporter(
  UserWaitPendingAction.LOAD_MORE_MESSAGES,
  2000,
)

async function loadPreviousMessages(iid: number) {
  const position = 'PREVIOUS'

  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-loadPreviousMessagesStart-iid`]: iid,
  })

  if (!(await shouldLoadMessages(position))) return
  loadMoreMessagesReporter.start()

  const roomId = getOpenedRoomId()
  const messages = await getMessages(roomId, {
    iid,
    position,
    count: BUCKET_SIZE,
  })

  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-loadPreviousMessagesGetMessages`]: true,
  })

  if (roomId === getOpenedRoomId()) insertMessagesToRoomPage(messages)

  loadChatUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-loadPreviousMessagesEnd-iid`]: iid,
  })

  loadMoreMessagesReporter.end({ position, roomId, count: BUCKET_SIZE })
}

async function loadNewMessages(iid: number) {
  const position = 'NEXT'
  if (!(await shouldLoadMessages(position))) return

  loadMoreMessagesReporter.start()

  const roomId = getOpenedRoomId()
  const messages = await getMessages(roomId, {
    iid,
    position,
    count: BUCKET_SIZE,
  })
  if (roomId === getOpenedRoomId()) insertMessagesToRoomPage(messages)

  loadMoreMessagesReporter.end({ position, roomId, count: BUCKET_SIZE })
}

export async function loadNextMessagesOfRoom() {
  const lastDisplayedMessageIid = R.last(
    openedRoomMessagesStorage.getAll(),
  )?.iid
  if (!lastDisplayedMessageIid) return
  await loadNewMessages(lastDisplayedMessageIid)
}

export async function loadPreviousMessagesOfRoom() {
  const firstDisplayedMessageIid = openedRoomMessagesStorage.getAll()?.[0].iid
  if (!firstDisplayedMessageIid) return
  await loadPreviousMessages(firstDisplayedMessageIid)
}

export async function scrollToMessageByIid(
  iid: number,
  {
    highlight,
    smooth,
    block,
  }: {
    highlight?: boolean
    smooth?: 'auto' | 'smooth'
    block?: 'center' | 'end' | 'nearest' | 'start'
  } = {
    highlight: true,
    smooth: 'smooth',
    block: 'center',
  },
  aquaManagerId = ROOM_MESSAGE_LIST_AQUA_MANAGER_ID,
  onAfterScrollEnd = () => {},
) {
  // NOTE: If message exists in openedRoomMessages, scroll smoothly.
  const message = openedRoomMessagesStorage.get(iid)
  const aquaManager = getAquaManager(aquaManagerId)

  if (message) {
    if (!message.patches?.deleted) {
      aquaManager?.scrollToById(
        message.id,
        {
          position: block || 'center',
          highlight,
          smooth: smooth === 'smooth',
        },
        undefined,
        onAfterScrollEnd,
      )
    } else {
      //  TODO: Show error
    }
  } else {
    const id = getOpenedRoomId()
    const fetchedMessage = await getMessageByIid(id, iid)

    if (fetchedMessage?.iid === iid && !fetchedMessage.patches?.deleted) {
      const showLoadingModal = !!openedRoomMessagesStorage.getAll().length

      aquaManager?.scrollToById(
        fetchedMessage.id,
        {
          position: block || 'center',
          highlight,
          smooth: smooth === 'smooth',
        },
        true,
        onAfterScrollEnd,
      )

      let loadingModalOpened = false
      await loadMessageFellows(iid, undefined, () => {
        if (showLoadingModal) {
          showLoadingMessagesModal()
          loadingModalOpened = true
        }
      })

      // TODO: smooth should not be undefined
      if (loadingModalOpened) closeCurrentModal()
    } else {
      //  TODO: Show error
    }
  }
}

export const loadLastMessageFellowsMeta = {
  loading: false,
  lastMessageIid: 0,
}
export async function onAfterMessagesSentToChat(
  waitForMessageToSend = false,
  messagesToForward?: Message[],
) {
  const { id: roomId } = (await getOpenedRoom()) || {}
  if (!roomId) return

  const goToBottom =
    !messagesToForward ||
    !!messagesToForward.find(message => message.roomId === roomId)

  if (!goToBottom) return

  const lastMessageIid = (await getRoomLastMessage(roomId))?.iid
  if (!lastMessageIid) return

  if (waitForMessageToSend)
    return updateLastMessageEvent.addEventListenerOnce(message => {
      goToLastMessage({ lastMessage: message })
    })

  const aquaManager = getAquaManager(ROOM_MESSAGE_LIST_AQUA_MANAGER_ID)!

  const isLastMessageRendered = openedRoomMessagesStorage.get(lastMessageIid)

  if (isLastMessageRendered) aquaManager.scrollToBottom(true, true, true)
  else {
    let loadingModalOpened = false
    loadLastMessageFellowsMeta.loading = true
    loadLastMessageFellowsMeta.lastMessageIid = lastMessageIid
    await loadMessageFellows(lastMessageIid, undefined, () => {
      showLoadingMessagesModal()
      loadingModalOpened = true
    })
    loadLastMessageFellowsMeta.loading = false

    if (loadingModalOpened) closeCurrentModal()
    aquaManager.scrollToBottom(true, true, false)
  }
}

export async function initiateRoomMessage(
  openingIid?: number,
  areMessagesPreloaded = false,
) {
  openedRoomStorage.setLoading(true)

  const { type, iid, id } = await getOpenedRoom()
  loadChatUserWaitReporter.start({ iid, roomId: id })
  const date = dateWithOffset()

  const lastMessage = areMessagesPreloaded
    ? R.last(openedRoomMessagesStorage.getAll())
    : await getRoomLastMessage(id)

  const savedScroll = getRoomLastScroll(id)
  const savedScrollIid =
    !savedScroll?.bottom && savedScroll?.topViewPortMessageIid

  const { seen } =
    type !== 'PRIVATE_SPACE'
      ? ((await getOpenedRoomData()) as Subscription | Pv | WappRoom) || {}
      : { seen: Infinity }

  loadChatUserWaitReporter.addTimeoutData({
    [`${date}-seen`]: seen,
    [`${date}-lastMessageIid`]: lastMessage?.iid,
    [`${date}-savedScrollIid`]: savedScrollIid,
    [`${date}-openingIid`]: openingIid,
  })

  if (!areMessagesPreloaded) {
    if (openingIid)
      // iid starts from 1
      await loadMessageFellows(openingIid)
    else if (savedScrollIid && lastMessage && lastMessage.iid <= seen)
      await loadMessageFellows(savedScrollIid, 'start')
    else if (type === 'PRIVATE_SPACE' || seen === iid || !seen)
      await loadPreviousMessages(iid + 1)
    else await loadMessageFellows(seen)
  }

  const firstUnseenMessageIid =
    seen &&
    openedRoomMessagesStorage
      .getAll()
      .find(msg => msg.iid > seen && !msg?.patches?.deleted)?.iid

  if (firstUnseenMessageIid)
    openedRoomStorage.setFirstUnseenMessageIid(firstUnseenMessageIid)
  else {
    // NOTE: Clear firstUnseenMessage of that has been set for last chat.
    openedRoomStorage.setFirstUnseenMessageIid(null)
  }

  openedRoomStorage.setLoading(false)

  let highlight = false
  let block: 'end' | 'start' | 'center' = 'end'

  let messageToScroll: number | null
  if (openingIid && !areMessagesPreloaded) {
    messageToScroll = openingIid
    highlight = true
    block = 'center'
  } else if (firstUnseenMessageIid) {
    messageToScroll = firstUnseenMessageIid
    block = 'end'
  } else if (savedScrollIid) {
    messageToScroll = savedScrollIid
    block = 'start'
  } else if (lastMessage?.iid) messageToScroll = lastMessage.iid
  else messageToScroll = null

  if (messageToScroll) {
    openedRoomStorage.setMessageToScroll(
      messageToScroll === lastMessage?.iid
        ? 'bottom'
        : {
            iid: messageToScroll,
            highlight,
            block,
            smooth: 'auto',
          },
    )
  }
}

interface GoToLastMessageOptions {
  block?: 'start' | 'end' | 'center'
  lastMessage?: Message
}
export async function goToLastMessage(
  { block = 'end', lastMessage }: GoToLastMessageOptions = { block: 'end' },
) {
  const getLastMessage = async () => {
    const { id } = (await getOpenedRoom()) || {}
    if (!id) return
    return await getRoomLastMessage(id)
  }
  const message = lastMessage || (await getLastMessage())

  if (message?.iid)
    await scrollToMessageByIid(message.iid, {
      highlight: false,
      smooth: 'smooth',
      block,
    })
}

export function handleDeletePatches({
  roomId,
  iids,
}: EventDeleteMessagesPayload) {
  // NOTE: filter messages of other rooms
  if (roomId === getOpenedRoomId()) {
    openedRoomMessagesStorage.deleteMessages(iids)
  }
}

export function handleEditPatches(
  edited: Pick<IMessage, 'iid' | 'roomId' | 'body' | 'attachment'>[],
) {
  // NOTE: filter messages of other rooms
  const messagesToEdit = edited.filter(R.propEq('roomId', getOpenedRoomId()))
  if (messagesToEdit.length) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    messagesToEdit.map(({ roomId, iid, ...patch }) =>
      openedRoomMessagesStorage.editMessage(iid, patch),
    )
  }
}

function filterMessagesToInsertInRoom(messagesToInsert: Message[]) {
  const sortedMessagesToInsert = R.sortBy(R.prop('iid'), messagesToInsert)

  const lastRenderedMessage = R.last(openedRoomMessagesStorage.getAll())
  if (!lastRenderedMessage) return sortedMessagesToInsert

  const continuesMessage = [lastRenderedMessage]
  // eslint-disable-next-line no-restricted-syntax
  for (const newMessage of sortedMessagesToInsert) {
    if (
      R.last(continuesMessage)?.iid === newMessage.iid - 1 ||
      (loadLastMessageFellowsMeta.loading &&
        loadLastMessageFellowsMeta.lastMessageIid === newMessage.iid - 1)
    )
      continuesMessage.push(newMessage)
    else break
  }

  return continuesMessage.slice(1)
}

export function handleNewMessages(messages: Message[], isFromServer = false) {
  // NOTE: filter messages of other rooms
  const openedRoomId = getOpenedRoomId()
  const openedRoomMessages = messages.filter(R.propEq('roomId', openedRoomId))
  const aquaManager = getAquaManager(ROOM_MESSAGE_LIST_AQUA_MANAGER_ID)
  if (openedRoomMessages.length) {
    const messagesToShow = filterMessagesToInsertInRoom(openedRoomMessages)

    if (messagesToShow.length) {
      if (isFromServer) aquaManager?.scrollToBottomIfIsAtBottom(true)
      openedRoomMessagesStorage.insert(messagesToShow)
    }
  }
}

export async function syncMessagesOfOpenedRoom(
  afterUpdateIid: number,
  beforeUpdateIid: number,
) {
  const messages = await getMessages(getOpenedRoomId(), {
    range: [beforeUpdateIid + 1, afterUpdateIid],
  })
  handleNewMessages(messages)
}

export async function editLastMessage() {
  // FIXME: Should pick from the most recent messages in the room rather than presenting messages.
  const messages = openedRoomMessagesStorage.getAll()

  // eslint-disable-next-line no-restricted-syntax
  for (const message of R.reverse(messages)) {
    // eslint-disable-next-line no-await-in-loop
    if (await canEditMessage(message)) {
      sendFieldApis.setEditContent(message as WithBodyMessage)
      break
    }
  }
}

export function deletedMessagesCount(start: number, end: number) {
  return openedRoomMessagesStorage
    .getAll()
    .filter(msg => msg.patches?.deleted && msg.iid >= start && msg.iid <= end)
    .length
}

export function clearOpenedRoomMessages() {
  openedRoomMessagesStorage.deleteAll()
}

export function fetchMessagesFromLastOpenedRoomMessageStorage(
  openingRoomId: string,
) {
  const lastMessages =
    lastOpenedRoomMessagesService.getLastOpenedRoomMessagesByRoomId(
      openingRoomId,
    )

  if (lastMessages) {
    openedRoomMessagesStorage.replace(lastMessages.messages)
    return true
  }
  return false
}
